import React from "react";
import usePageContent from "../../hooks/usePageContent";
import EditableSection from "../../components/CMS/EditableSection";
import "./LabsPage.css";

const LabsPage = ({ user }) => {
    const { sections, get, loading, error, refresh } =
        usePageContent("labs");

    const editable = user?.role === "admin";

    const header = get("header");
    const labs = get("labs");

    if (loading && sections.length === 0)
        return <div>Loading Labs...</div>;

    if (error && sections.length === 0)
        return <div>Error: {error}</div>;

    const getSection = key =>
        sections.find(s => s.section_key === key);

    const headerSection = getSection("header");
    const labsSection = getSection("labs");

    return (
        <div className="labs-page">

            {headerSection && (
                <EditableSection
                    uniqueId={headerSection.unique_id}
                    title={headerSection.title}
                    data={header}
                    editable={editable}
                    Editor={LabsHeaderEditor}
                    onSaved={refresh}
                    Display={({ data }) => (
                        <section className="labs-header">
                            <div className="container">
                                <h2 className="page_title">
                                    {data.title}
                                </h2>
                            </div>
                        </section>
                    )}
                />
            )}

            {labsSection && (
                <EditableSection
                    uniqueId={labsSection.unique_id}
                    title={labsSection.title}
                    data={labs}
                    editable={editable}
                    Editor={LabsListEditor}
                    onSaved={refresh}
                    Display={({ data }) => (
                        <section className="labs-content">
                            <div className="container">
                                <div className="row g-4 row-cols-1 row-cols-md-4">

                                    {(data.items || []).map((item, index) => (
                                        <div className="col" key={index}>

                                            <div className="lab-card">

                                                {item.image && (
                                                    <img
                                                        src={item.image}
                                                        className="lab-image"
                                                        alt={
                                                            item.alt ||
                                                            item.title
                                                        }
                                                    />
                                                )}

                                                <div className="lab-body">

                                                    <h5 className="lab-title">
                                                        {item.title}
                                                    </h5>

                                                    <div className="lab-description">
                                                        <p>
                                                            {item.description}
                                                        </p>
                                                    </div>

                                                    {item.url && (
                                                       
                                                        <a
                                                            href={item.url}
                                                            className="btn btn-outline-info"
                                                        >
                                                            {item.buttonText ||
                                                                "Explore Lab"}
                                                        </a>
                                                    
                                                    )}
                                                     {item.url2 && (
                                                      
                                                        <a
                                                            href={item.url2}
                                                            className="btn btn-outline-info"
                                                        >
                                                            {item.buttonText2 ||
                                                                "Start Project"}
                                                        </a>
                                            
                                                      
                                                    )}

                                                </div>

                                            </div>

                                        </div>
                                    ))}

                                </div>
                            </div>
                        </section>
                    )}
                />
            )}

        </div>
    );
};


/* =========================
   HEADER EDITOR
========================= */

const LabsHeaderEditor = ({ data, update }) => (
    <div className="cms-editor">

        <label>Title</label>

        <input
            value={data.title || ""}
            onChange={e =>
                update("title", e.target.value)
            }
        />

    </div>
);


/* =========================
   LABS EDITOR
========================= */

const LabsListEditor = ({
    data,
    update,
    updateItem,
    addItem,
    removeItem
}) => {

    const [uploading, setUploading] = React.useState({});

    const uploadImage = async (index, file) => {

        if (!file) return;

        try {

            setUploading(prev => ({
                ...prev,
                [index]: true
            }));

            const formData = new FormData();

            formData.append("image", file);

            const response = await fetch(
                "/api/content/upload-image",
                {
                    method: "POST",
                    body: formData
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error ||
                    "Image upload failed"
                );
            }

            updateItem(
                index,
                "image",
                result.url
            );

        } catch (error) {

            console.error(error);

            alert(
                error.message ||
                "Failed to upload image"
            );

        } finally {

            setUploading(prev => ({
                ...prev,
                [index]: false
            }));

        }
    };

    return (
        <div className="cms-editor">

            <label>Section Title</label>

            <input
                value={data.title || ""}
                onChange={e =>
                    update("title", e.target.value)
                }
            />

            {(data.items || []).map((item, index) => (

                <div
                    className="cms-item"
                    key={index}
                >

                    <div className="cms-item-header">

                        <strong>
                            Lab {index + 1}
                        </strong>

                        <button
                            type="button"
                            className="cms-delete"
                            onClick={() =>
                                removeItem(index)
                            }
                        >
                            Delete
                        </button>

                    </div>


                    <label>Lab Name</label>

                    <input
                        value={item.title || ""}
                        onChange={e =>
                            updateItem(
                                index,
                                "title",
                                e.target.value
                            )
                        }
                    />


                    <label>Description</label>

                    <textarea
                        value={item.description || ""}
                        onChange={e =>
                            updateItem(
                                index,
                                "description",
                                e.target.value
                            )
                        }
                    />


                    <label>Lab Image</label>

                    {item.image && (
                        <img
                            src={item.image}
                            alt={
                                item.alt ||
                                item.title ||
                                "Lab"
                            }
                            className="cms-image-preview"
                        />
                    )}

                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        disabled={uploading[index]}
                        onChange={e =>
                            uploadImage(
                                index,
                                e.target.files?.[0]
                            )
                        }
                    />

                    {uploading[index] && (
                        <div>Uploading...</div>
                    )}


                    <label>Image Alt Text</label>

                    <input
                        value={item.alt || ""}
                        onChange={e =>
                            updateItem(
                                index,
                                "alt",
                                e.target.value
                            )
                        }
                    />


                    <label>Button Text</label>

                    <input
                        value={item.buttonText || ""}
                        onChange={e =>
                            updateItem(
                                index,
                                "buttonText",
                                e.target.value
                            )
                        }
                    />


                    <label>Button URL</label>

                    <input
                        value={item.url || ""}
                        onChange={e =>
                            updateItem(
                                index,
                                "url",
                                e.target.value
                            )
                        }
                    />

                </div>

            ))}


            <button
                type="button"
                className="cms-add"
                onClick={() =>
                    addItem({
                        title: "",
                        description: "",
                        image: "",
                        alt: "",
                        buttonText: "Explore Lab",
                        url: ""
                    })
                }
            >
                + Add Lab
            </button>

        </div>
    );
};


export default LabsPage;